/**
 * Wishlist drawer
 * Figma: desktop 495:32, mobile 497:32.
 * Keeps legacy handle-only rows working while preserving exact variant options
 * for new saves: { handle, variantId, options: { colour, size, orientation } }.
 */
(function () {
  'use strict';

  var drawer = document.getElementById('wishlist-drawer');
  var overlay = document.getElementById('wishlist-drawer-overlay');
  var toggleBtn = document.getElementById('wishlist-drawer-toggle');
  var closeBtn = document.getElementById('wishlist-drawer-close');
  var shareBtn = document.getElementById('wishlist-drawer-share');
  var shareSheet = document.getElementById('wishlist-share-sheet');
  var shareCopyBtn = document.getElementById('wishlist-share-copy');
  var shareMessageBtn = document.getElementById('wishlist-share-message');
  var countEl = document.getElementById('wishlist-drawer-count');
  var emptyEl = document.getElementById('wishlist-drawer-empty');
  var itemsEl = document.getElementById('wishlist-drawer-items');
  var loadingEl = document.getElementById('wishlist-drawer-loading');
  var noticeEl = document.getElementById('wishlist-drawer-notice');
  var noticeTextEl = document.getElementById('wishlist-drawer-notice-text');
  var footerEl = document.getElementById('wishlist-drawer-footer');
  var footerCountEl = document.getElementById('wishlist-drawer-footer-count');
  var bulkBtn = document.getElementById('wishlist-drawer-bulk');
  var snackbarEl = document.getElementById('wishlist-drawer-snackbar');
  var snackbarTextEl = document.getElementById('wishlist-drawer-snackbar-text');
  var undoBtn = document.getElementById('wishlist-drawer-undo');
  var dragHandle = drawer && drawer.querySelector('.aykah-wishlist-drag-handle');
  var headerEl = drawer && drawer.querySelector('.aykah-wishlist-header');

  if (!drawer || !overlay || !toggleBtn || !closeBtn) return;
  if (drawer.getAttribute('data-wishlist-bound') === 'true') return;
  drawer.setAttribute('data-wishlist-bound', 'true');

  var isOpen = false;
  var triggerElement = null;
  var renderToken = 0;
  var readyItems = [];
  var undoSnapshot = null;
  var undoTimer = null;
  var pendingRemovalFocusKey = null;
  var pendingRemovalFocusUndo = false;
  var shareCloseTimer = null;
  var pickerCloseTimer = null;
  var activePicker = null;
  var pickerResume = null;
  var productRecords = Object.create(null);
  var productSwatchRequests = Object.create(null);

  var CART_ICON = '<svg viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M230.14,58.87A8,8,0,0,0,224,56H62.68L56.6,22.57A8,8,0,0,0,48.73,16H24a8,8,0,0,0,0,16h18L67.56,172.29a24,24,0,0,0,5.33,11.27,28,28,0,1,0,44.4,8.44h45.42A27.75,27.75,0,0,0,160,204a28,28,0,1,0,28-28H91.17a8,8,0,0,1-7.87-6.57L80.13,152h116a24,24,0,0,0,23.61-19.71l12.16-66.86A8,8,0,0,0,230.14,58.87ZM104,204a12,12,0,1,1-12-12A12,12,0,0,1,104,204Zm96,0a12,12,0,1,1-12-12A12,12,0,0,1,200,204Zm4-74.57A8,8,0,0,1,196.1,136H77.22L65.59,72H214.41Z"/></svg>';
  var IMAGE_ICON = '<svg viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40Zm0,16V158.75l-26.07-26.06a16,16,0,0,0-22.63,0l-20,20-44-44a16,16,0,0,0-22.62,0L40,149.37V56ZM40,172l52-52,80,80H40Zm176,28H194.63l-36-36,20-20L216,181.38V200ZM144,100a12,12,0,1,1,12,12A12,12,0,0,1,144,100Z"/></svg>';

  function escHtml(value) {
    var el = document.createElement('div');
    el.textContent = value == null ? '' : String(value);
    return el.innerHTML;
  }

  function escAttr(value) {
    return escHtml(value).replace(/`/g, '&#96;');
  }

  function savedHandle(saved) {
    return typeof saved === 'string' ? saved : saved && typeof saved.handle === 'string' ? saved.handle : '';
  }

  function normalizeOptions(options) {
    var source = options && typeof options === 'object' ? options : {};
    var normalized = {};
    var colour = source.colour || source.color;
    if (typeof colour === 'string' && colour.trim()) normalized.colour = colour.trim();
    ['size', 'orientation', 'option1', 'option2', 'option3'].forEach(function (key) {
      if (typeof source[key] === 'string' && source[key].trim()) normalized[key] = source[key].trim();
    });
    return normalized;
  }

  function normalizeSaved(saved) {
    var handle = savedHandle(saved);
    if (!handle) return null;
    if (typeof saved === 'string') return { handle: handle, variantId: null, options: {}, partial: true, legacy: true };
    var parsedVariant = parseInt(saved.variantId, 10);
    var options = normalizeOptions(saved.options);
    return {
      handle: handle,
      variantId: parsedVariant || null,
      options: options,
      partial: saved.partial === true || (!parsedVariant && Object.keys(options).length === 0),
      legacy: false
    };
  }

  function savedRecordScore(saved) {
    return (saved.variantId ? 100 : 0) + Object.keys(saved.options || {}).length * 10 + (saved.partial ? 0 : 1);
  }

  function wishlistRecordKey(saved) {
    var handle = savedHandle(saved);
    if (!handle) return '';
    var variantId = saved && typeof saved === 'object' ? parseInt(saved.variantId, 10) || null : null;
    var isPartial = typeof saved === 'string' || !variantId || (saved && saved.partial === true);
    return handle + (isPartial ? '::partial' : '::variant:' + variantId);
  }

  function wishlistDomToken(savedOrKey) {
    var key = typeof savedOrKey === 'string' && savedOrKey.indexOf('::') > -1
      ? savedOrKey
      : wishlistRecordKey(savedOrKey);
    return String(key || 'saved-item').replace(/[^a-zA-Z0-9_-]/g, '-');
  }

  function normalizeWishlist(items) {
    var normalized = [];
    var positions = Object.create(null);

    (Array.isArray(items) ? items : []).forEach(function (raw) {
      var saved = normalizeSaved(raw);
      if (!saved) return;
      var key = wishlistRecordKey(saved);
      var existingPosition = positions[key];

      if (existingPosition === undefined) {
        positions[key] = normalized.length;
        normalized.push(saved);
      } else if (savedRecordScore(saved) >= savedRecordScore(normalized[existingPosition])) {
        normalized[existingPosition] = saved;
      }
    });
    var handlesWithExactVariants = Object.create(null);
    normalized.forEach(function (saved) {
      if (saved.variantId && !saved.partial) handlesWithExactVariants[saved.handle] = true;
    });
    return normalized.filter(function (saved) {
      return !(saved.partial && handlesWithExactVariants[saved.handle]);
    });
  }

  function getWishlist() {
    try {
      var stored = JSON.parse(localStorage.getItem('aykah_wishlist') || '[]');
      var normalized = normalizeWishlist(stored);
      var serialized = normalized.map(serializableSaved);
      if (JSON.stringify(stored) !== JSON.stringify(serialized)) {
        localStorage.setItem('aykah_wishlist', JSON.stringify(serialized));
      }
      return normalized;
    } catch (e) {
      return [];
    }
  }

  function serializableSaved(saved) {
    return {
      handle: saved.handle,
      variantId: saved.variantId || null,
      options: normalizeOptions(saved.options),
      partial: saved.partial === true
    };
  }

  function setWishlist(items) {
    var normalized = normalizeWishlist(items);
    localStorage.setItem('aykah_wishlist', JSON.stringify(normalized.map(serializableSaved)));
    document.dispatchEvent(new CustomEvent('wishlist:updated'));
    return normalized;
  }

  function updateHeaderBadge() {
    var count = getWishlist().length;
    var iconWrap = toggleBtn.querySelector('[data-icon-wrap]') || toggleBtn.querySelector('.relative');
    if (!iconWrap) return;
    var badge = iconWrap.querySelector('[data-wishlist-badge]');

    if (count > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.setAttribute('data-wishlist-badge', '');
        badge.style.cssText = 'position:absolute;top:-8px;right:-8px;width:16px;height:16px;background:#7D6040;color:#fff;font-size:10px;font-weight:600;border-radius:9999px;display:flex;align-items:center;justify-content:center;line-height:1;pointer-events:none';
        iconWrap.appendChild(badge);
      }
      badge.textContent = count;
    } else if (badge) {
      badge.remove();
    }
  }

  function updateCount(count) {
    if (countEl) countEl.textContent = count > 0 ? count + ' saved' : 'Empty';
    if (shareBtn) shareBtn.hidden = count < 1;
    if (count < 1) closeShareSheet(false);
  }

  function formatPrice(price) {
    var num = parseFloat(price);
    if (isNaN(num)) return '$0.00';
    var cents = Math.round(num * 100);
    return window.AykahFormatMoney ? window.AykahFormatMoney(cents) : '$' + (cents / 100).toFixed(2);
  }

  function formatDropAmount(amount) {
    var num = Math.max(0, parseFloat(amount) || 0);
    return '$' + (Math.round(num * 100) % 100 === 0 ? String(Math.round(num)) : num.toFixed(2));
  }

  function normalizeAjaxPrice(price) {
    if (price == null || price === '') return null;
    var cents = parseFloat(price);
    return isNaN(cents) ? price : (cents / 100).toFixed(2);
  }

  function normalizeAjaxProduct(product) {
    var normalized = Object.assign({}, product);

    normalized.variants = (product.variants || []).map(function (variant) {
      var copy = Object.assign({}, variant);
      copy.price = normalizeAjaxPrice(variant.price);
      copy.compare_at_price = normalizeAjaxPrice(variant.compare_at_price);
      copy.available = variant.available === false || variant.available === 'false' ? false : true;
      if (typeof copy.featured_image === 'string') copy.featured_image = { src: copy.featured_image };
      return copy;
    });

    normalized.images = (product.images || []).map(function (image) {
      return typeof image === 'string' ? { src: image } : image;
    });

    return normalized;
  }

  function fetchProduct(handle) {
    var controller = typeof window.AbortController === 'function' ? new window.AbortController() : null;
    var timeoutId = controller ? window.setTimeout(function () { controller.abort(); }, 10000) : null;
    var options = { credentials: 'same-origin' };

    if (controller) options.signal = controller.signal;

    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
    var request = fetch(root + 'products/' + encodeURIComponent(handle) + '.js', options)
      .then(function (res) {
        if (res.status === 404) {
          var missingError = new Error('Product not found');
          missingError.code = 'PRODUCT_NOT_FOUND';
          throw missingError;
        }

        if (!res.ok) {
          var responseError = new Error('Could not load product');
          responseError.code = 'PRODUCT_FETCH_FAILED';
          responseError.status = res.status;
          throw responseError;
        }

        return res.json();
      })
      .then(function (data) {
        if (!data || !Array.isArray(data.variants)) {
          var dataError = new Error('Invalid product response');
          dataError.code = 'PRODUCT_FETCH_FAILED';
          throw dataError;
        }

        return normalizeAjaxProduct(data);
      });

    return request.then(
      function (product) {
        if (timeoutId) window.clearTimeout(timeoutId);
        return product;
      },
      function (error) {
        if (timeoutId) window.clearTimeout(timeoutId);
        throw error;
      }
    );
  }

  function swatchLookupKey(value) {
    return String(value == null ? '' : value).trim().toLowerCase().replace(/\s+/g, ' ');
  }

  function safePdpSwatchColour(value) {
    var colour = String(value || '').trim();
    if (/^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(colour)) return colour;
    if (/^(?:rgb|rgba|hsl|hsla)\(\s*[-+\d.%\s,/]+\)$/i.test(colour)) return colour;

    var variable = colour.match(/^var\(\s*(--[a-z0-9_-]+)\s*,\s*(.+)\)$/i);
    if (variable) {
      var fallback = safePdpSwatchColour(variable[2]);
      if (fallback && fallback.indexOf('var(') !== 0) {
        return 'var(' + variable[1] + ', ' + fallback + ')';
      }
    }
    return '';
  }

  function pdpSwatchesFromHtml(html) {
    if (typeof window.DOMParser !== 'function') return {};
    var documentFromPdp = new window.DOMParser().parseFromString(String(html || ''), 'text/html');
    var swatches = {};

    Array.prototype.forEach.call(documentFromPdp.querySelectorAll('[data-color-swatch][data-color-value]'), function (button) {
      var value = button.getAttribute('data-color-value');
      var dot = button.querySelector('.color-pill-dot');
      var inlineStyle = dot ? dot.getAttribute('style') || '' : '';
      var colourMatch = inlineStyle.match(/(?:^|;)\s*background-color\s*:\s*([^;]+)/i);
      var colour = safePdpSwatchColour(colourMatch ? colourMatch[1] : '');
      if (value && colour) swatches[swatchLookupKey(value)] = { colour: colour };
    });

    return swatches;
  }

  function fetchProductSwatches(handle) {
    if (productSwatchRequests[handle]) return productSwatchRequests[handle];

    var controller = typeof window.AbortController === 'function' ? new window.AbortController() : null;
    var timeoutId = controller ? window.setTimeout(function () { controller.abort(); }, 10000) : null;
    var options = { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } };
    if (controller) options.signal = controller.signal;

    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
    var productUrl = root + 'products/' + encodeURIComponent(handle);
    var sectionUrl = productUrl + '?section_id=product-hero';
    try {
      var previewThemeId = new window.URLSearchParams(window.location.search).get('preview_theme_id');
      if (previewThemeId) {
        sectionUrl += '&preview_theme_id=' + encodeURIComponent(previewThemeId);
        productUrl += '?preview_theme_id=' + encodeURIComponent(previewThemeId);
      }
    } catch (e) {
      /* Older browsers still receive the active storefront theme via cookies. */
    }

    var request = fetch(sectionUrl, options)
      .then(function (response) {
        if (!response.ok) throw new Error('Could not load product swatches');
        return response.text();
      })
      .then(function (html) {
        var swatches = pdpSwatchesFromHtml(html);
        if (Object.keys(swatches).length) return swatches;

        /* Alternate product templates can use a different section instance ID.
           In that uncommon case, inspect the actual PDP rather than guessing. */
        return fetch(productUrl, options)
          .then(function (response) {
            if (!response.ok) throw new Error('Could not load product page swatches');
            return response.text();
          })
          .then(function (productHtml) {
            var pageSwatches = pdpSwatchesFromHtml(productHtml);
            if (!Object.keys(pageSwatches).length) throw new Error('Product swatches were not found');
            return pageSwatches;
          });
      });

    productSwatchRequests[handle] = request.then(
      function (swatches) {
        if (timeoutId) window.clearTimeout(timeoutId);
        return swatches;
      },
      function (error) {
        if (timeoutId) window.clearTimeout(timeoutId);
        delete productSwatchRequests[handle];
        throw error;
      }
    );
    return productSwatchRequests[handle];
  }

  function lowestVariant(product) {
    var variants = product && product.variants ? product.variants.slice() : [];
    if (!variants.length) return null;
    return variants.sort(function (a, b) { return parseFloat(a.price) - parseFloat(b.price); })[0];
  }

  function optionKey(name, position, product, option) {
    var normalized = String(name || '').toLowerCase();
    if (/colou?r|finish|fabric|material/.test(normalized)) return 'colour';
    if (/size|seater/.test(normalized)) return 'size';
    if (/orientation|configuration|facing|chaise|direction/.test(normalized)) return 'orientation';

    var values = option && Array.isArray(option.values) ? option.values.slice() : [];
    if (!values.length) {
      (product && product.variants ? product.variants : []).forEach(function (variant) {
        var value = variant['option' + position];
        if (value && values.indexOf(value) === -1) values.push(value);
      });
    }
    if (product && /sectional/i.test(product.title || '')) {
      var hasLeft = values.some(function (value) { return /(^|\b)(left|laf|lhf|lhs)(\b|$)/i.test(String(value)); });
      var hasRight = values.some(function (value) { return /(^|\b)(right|raf|rhf|rhs)(\b|$)/i.test(String(value)); });
      if (hasLeft && hasRight) return 'orientation';
    }
    return 'option' + position;
  }

  function optionDescriptors(product) {
    var usedKeys = Object.create(null);
    return (product && product.options ? product.options : []).map(function (option, index) {
      var name = typeof option === 'string' ? option : option && option.name;
      var position = option && option.position ? parseInt(option.position, 10) : index + 1;
      var key = optionKey(name, position, product, option);
      if (usedKeys[key]) key = 'option' + position;
      usedKeys[key] = true;
      return { key: key, name: name || '', position: position || index + 1 };
    }).filter(function (descriptor) {
      if (descriptor.position < 1 || descriptor.position > 3) return false;
      var values = [];
      (product.variants || []).forEach(function (variant) {
        var value = variantOption(variant, descriptor);
        if (value && values.indexOf(value) === -1) values.push(value);
      });
      return !(values.length === 1 && values[0] === 'Default Title');
    });
  }

  function variantOption(variant, descriptor) {
    return variant ? variant['option' + descriptor.position] || '' : '';
  }

  function descriptorIsSelectable(product, descriptor) {
    var seen = {};
    (product && product.variants ? product.variants : []).forEach(function (variant) {
      var value = variantOption(variant, descriptor);
      if (value) seen[value] = true;
    });
    return Object.keys(seen).length > 1;
  }

  function findVariant(product, saved, descriptors) {
    var variants = product && product.variants ? product.variants : [];
    var byId = saved.variantId ? variants.find(function (variant) {
      return parseInt(variant.id, 10) === parseInt(saved.variantId, 10);
    }) : null;
    if (byId) return byId;

    var optionKeys = Object.keys(saved.options || {});
    if (!optionKeys.length) return variants[0] || null;
    return variants.find(function (variant) {
      return descriptors.every(function (descriptor) {
        return !saved.options[descriptor.key] || variantOption(variant, descriptor) === saved.options[descriptor.key];
      });
    }) || variants[0] || null;
  }

  function priceVariantForSelection(product, descriptors, meta, fallback) {
    var chosen = {};
    meta.forEach(function (part) {
      if (!part.missing) chosen[part.key] = part.value;
    });
    var candidates = (product.variants || []).filter(function (variant) {
      return descriptors.every(function (descriptor) {
        return !chosen[descriptor.key] || variantOption(variant, descriptor) === chosen[descriptor.key];
      });
    });
    if (!candidates.length) return fallback || lowestVariant(product);
    return candidates.sort(function (a, b) { return parseFloat(a.price) - parseFloat(b.price); })[0];
  }

  function selectedOptionsFromMeta(meta) {
    var selected = {};
    (meta || []).forEach(function (part) {
      if (!part.missing && part.key) selected[part.key] = part.value;
    });
    return selected;
  }

  function matchingVariants(product, descriptors, selectedOptions, overrideKey, overrideValue) {
    return (product && product.variants ? product.variants : []).filter(function (variant) {
      return descriptors.every(function (descriptor) {
        var expected = descriptor.key === overrideKey ? overrideValue : selectedOptions[descriptor.key];
        return !expected || variantOption(variant, descriptor) === expected;
      });
    });
  }

  function bestMatchingVariant(product, descriptors, selectedOptions, overrideKey, overrideValue) {
    var matches = matchingVariants(product, descriptors, selectedOptions, overrideKey, overrideValue);
    var available = matches.filter(function (variant) { return !!variant.available; });
    var candidates = available.length ? available : matches;
    if (!candidates.length) return null;
    return candidates.slice().sort(function (a, b) {
      return parseFloat(a.price) - parseFloat(b.price);
    })[0];
  }

  function descriptorValues(product, descriptors, descriptor, selectedOptions) {
    var seen = {};
    var values = [];
    (product && product.variants ? product.variants : []).forEach(function (variant) {
      var value = variantOption(variant, descriptor);
      if (!value || seen[value]) return;
      seen[value] = true;

      var compatibleMatches = matchingVariants(product, descriptors, selectedOptions, descriptor.key, value);
      var valueMatches = matchingVariants(product, descriptors, {}, descriptor.key, value);
      var compatibleAvailable = compatibleMatches.some(function (candidate) { return !!candidate.available; });
      var valueAvailable = valueMatches.some(function (candidate) { return !!candidate.available; });
      var priced = bestMatchingVariant(product, descriptors, selectedOptions, descriptor.key, value) ||
        bestMatchingVariant(product, descriptors, {}, descriptor.key, value) ||
        compatibleMatches[0] ||
        valueMatches[0] ||
        variant;

      values.push({
        value: value,
        available: compatibleAvailable || valueAvailable,
        price: priced ? parseFloat(priced.price) : 0
      });
    });
    return values;
  }

  function missingLabel(key) {
    if (key === 'size') return 'Size not chosen';
    if (key === 'colour') return 'Colour not chosen';
    if (key === 'orientation') return 'Orientation not chosen';
    return 'Option not chosen';
  }

  function choiceLabelFor(missing, selectedUnavailable) {
    if (selectedUnavailable) return 'Choose options';
    if (missing.length === 1 && missing[0].key === 'size') return 'Choose size';
    if (missing.length === 1 && missing[0].key === 'colour') return 'Choose colour';
    return 'Choose options';
  }

  function canUseQuickPicker(descriptors) {
    if (!descriptors.length || descriptors.length > 2) return false;
    return descriptors.every(function (descriptor) {
      return descriptor.key === 'size' || descriptor.key === 'colour';
    });
  }

  function compactPrice(price) {
    var num = parseFloat(price);
    if (isNaN(num)) return '';
    var rounded = Math.round(num * 100) / 100;
    return '$' + (rounded % 1 === 0 ? String(rounded) : rounded.toFixed(2));
  }

  function sizeChoiceWidth(value) {
    var normalized = String(value || '').toLowerCase();
    if (normalized === 'double') return 82;
    if (normalized === 'queen') return 76;
    if (normalized === 'king') return 66;
    return Math.max(66, Math.min(112, 42 + String(value || '').length * 7));
  }

  function productViewModel(product, saved) {
    var handle = saved.handle;
    var recordKey = wishlistRecordKey(saved);
    var variants = product.variants || [];
    var first = variants[0] || null;
    var descriptors = optionDescriptors(product);
    var selectableDescriptors = descriptors.filter(function (descriptor) {
      return descriptorIsSelectable(product, descriptor);
    });
    var hasSelectableOptions = selectableDescriptors.length > 0;
    var selected = findVariant(product, saved, descriptors);
    var selectedWasRemoved = !!(saved.variantId && !variants.some(function (variant) {
      return parseInt(variant.id, 10) === parseInt(saved.variantId, 10);
    }));
    var selectedUnavailable = hasSelectableOptions && (selectedWasRemoved || !!(selected && !selected.available && !saved.legacy));
    var meta = [];

    if (!selectedUnavailable) {
      descriptors.forEach(function (descriptor) {
        var savedValue = saved.options[descriptor.key] || '';
        var isSelectable = selectableDescriptors.some(function (candidate) { return candidate.key === descriptor.key; });
        var omitForPartialCard = saved.partial && isSelectable && !savedValue;
        var omitForLegacySize = saved.legacy && isSelectable && descriptor.key === 'size';
        var value = savedValue || (!omitForPartialCard && !omitForLegacySize ? variantOption(selected, descriptor) : '');
        meta.push({
          key: descriptor.key,
          value: value || missingLabel(descriptor.key),
          missing: !value
        });
      });
    }

    var missing = meta.filter(function (part) { return part.missing; });
    var needsChoice = hasSelectableOptions && (selectedUnavailable || missing.length > 0);
    var displayMeta = missing.length > 1
      ? [{ key: 'options', value: 'Options not chosen', missing: true }]
      : meta;
    var pickerDescriptors = selectedUnavailable
      ? selectableDescriptors.slice()
      : missing.map(function (part) {
          return descriptors.find(function (descriptor) { return descriptor.key === part.key; });
        }).filter(Boolean);
    var quickPicker = needsChoice && canUseQuickPicker(pickerDescriptors);
    var choiceLabel = choiceLabelFor(missing, selectedUnavailable);
    var priceVariant = needsChoice ? priceVariantForSelection(product, descriptors, meta, selected || first) : selected || first;
    var selectedImage = selected && selected.featured_image ? selected.featured_image.src : '';
    var imageSrc = selectedImage || (product.images && product.images[0] ? product.images[0].src : '');
    var image = imageSrc ? imageSrc + (imageSrc.indexOf('?') > -1 ? '&width=200' : '?width=200') : '';

    var price = priceVariant ? parseFloat(priceVariant.price) : 0;
    var compare = priceVariant && priceVariant.compare_at_price ? parseFloat(priceVariant.compare_at_price) : 0;
    var hasDrop = !!(compare && compare > price);
    var ready = !!(selected && selected.available && !needsChoice);

    return {
      recordKey: recordKey,
      handle: handle,
      title: product.title || handle,
      image: image,
      meta: selectedUnavailable ? [{ key: 'unavailable', value: 'Selected option unavailable', missing: true }] : displayMeta,
      needsChoice: needsChoice,
      choiceLabel: choiceLabel,
      quickPicker: quickPicker,
      descriptors: descriptors,
      pickerDescriptors: pickerDescriptors,
      selectedOptions: selectedOptionsFromMeta(meta),
      variantId: selected ? selected.id : null,
      available: !!(selected && selected.available),
      ready: ready,
      pricePrefix: needsChoice && (selectedUnavailable || missing.some(function (part) { return part.key === 'size'; })),
      price: price,
      comparePrice: compare,
      hasDrop: hasDrop,
      dropAmount: hasDrop ? compare - price : 0,
      saved: saved
    };
  }

  function priceMarkup(item) {
    if (item.pricePrefix) {
      return '<div class="aykah-wishlist-price"><span class="aykah-wishlist-price-main">from ' + escHtml(formatPrice(item.price)) + '</span></div>';
    }
    if (item.hasDrop) {
      return '<div class="aykah-wishlist-price has-drop">' +
        '<span class="aykah-wishlist-price-drop">Price drop</span>' +
        '<span class="aykah-wishlist-price-old">' + escHtml(formatPrice(item.comparePrice)) + '</span>' +
        '<span class="aykah-wishlist-price-main">' + escHtml(formatPrice(item.price)) + '</span>' +
      '</div>';
    }
    return '<div class="aykah-wishlist-price"><span class="aykah-wishlist-price-main">' + escHtml(formatPrice(item.price)) + '</span></div>';
  }

  function actionMarkup(item) {
    if (item.needsChoice) {
      if (item.quickPicker) {
        var pickerId = 'wishlist-option-picker-' + wishlistDomToken(item.recordKey);
        return '<button type="button" class="aykah-wishlist-row-action wishlist-drawer-choose" data-wishlist-key="' + escAttr(item.recordKey) + '" aria-expanded="false" aria-controls="' + escAttr(pickerId) + '">' + escHtml(item.choiceLabel) + '</button>';
      }
      return '<a class="aykah-wishlist-row-action" href="/products/' + encodeURIComponent(item.handle) + '">' + escHtml(item.choiceLabel) + '</a>';
    }
    if (!item.available) {
      return '<a class="aykah-wishlist-row-action" href="/products/' + encodeURIComponent(item.handle) + '">View item</a>';
    }
    return '<button type="button" class="aykah-wishlist-row-action wishlist-drawer-atc" data-variant-id="' + escAttr(item.variantId) + '" data-wishlist-key="' + escAttr(item.recordKey) + '" data-title="' + escAttr(item.title) + '">' + CART_ICON + '<span>Add to cart</span></button>';
  }

  function optionMetaMarkup(item) {
    if (!item.meta || !item.meta.length) return '<div class="aykah-wishlist-variant"></div>';
    var parts = item.meta.map(function (part) {
      return '<span' + (part.missing ? ' class="is-missing"' : '') + '>' + escHtml(part.value) + '</span>';
    });
    return '<div class="aykah-wishlist-variant" role="status" aria-live="polite">' + parts.join('<span aria-hidden="true"> · </span>') + '</div>';
  }

  function itemMarkup(item) {
    var productUrl = '/products/' + encodeURIComponent(item.handle) + (item.variantId ? '?variant=' + encodeURIComponent(item.variantId) : '');
    var pickerId = 'wishlist-option-picker-' + wishlistDomToken(item.recordKey);
    return '<article class="aykah-wishlist-item" data-wishlist-item="' + escAttr(item.recordKey) + '">' +
      '<a href="' + productUrl + '" class="aykah-wishlist-thumb" aria-label="' + escAttr(item.title) + '">' +
        (item.image
          ? '<img src="' + escAttr(item.image) + '" alt="' + escAttr(item.title) + '" loading="lazy">'
          : '<span class="aykah-wishlist-thumb-placeholder">' + IMAGE_ICON + '</span>') +
      '</a>' +
      '<a href="' + productUrl + '" class="aykah-wishlist-name">' + escHtml(item.title) + '</a>' +
      optionMetaMarkup(item) +
      priceMarkup(item) +
      '<div class="aykah-wishlist-actions">' +
        actionMarkup(item) +
        '<button type="button" class="aykah-wishlist-remove wishlist-drawer-remove" data-wishlist-key="' + escAttr(item.recordKey) + '" aria-label="Remove this ' + escAttr(item.title) + ' variation from wishlist">Remove</button>' +
      '</div>' +
      '<div class="aykah-wishlist-option-picker" id="' + escAttr(pickerId) + '" role="dialog" aria-modal="false" hidden></div>' +
    '</article>';
  }

  function findItemElement(recordKey) {
    if (!itemsEl) return null;
    return Array.prototype.slice.call(itemsEl.querySelectorAll('[data-wishlist-item]')).find(function (element) {
      return element.getAttribute('data-wishlist-item') === recordKey;
    }) || null;
  }

  function pickerTitle(descriptors) {
    if (descriptors.length === 1 && descriptors[0].key === 'size') return 'Choose size';
    if (descriptors.length === 1 && descriptors[0].key === 'colour') return 'Choose colour';
    return 'Choose options';
  }

  function optionButtonMarkup(choice, descriptor, selectedValue, exactSwatches) {
    var selected = selectedValue === choice.value;
    var swatch = descriptor.key === 'colour' && exactSwatches
      ? exactSwatches[swatchLookupKey(choice.value)]
      : null;
    var hasExactSwatch = !!(swatch && swatch.colour);
    var className = 'aykah-wishlist-option-choice' +
      (selected ? ' is-selected' : '') +
      (descriptor.key === 'colour' && !hasExactSwatch ? ' is-name-fallback' : '');
    var common = ' type="button" class="' + className + '"' +
      ' data-option-key="' + escAttr(descriptor.key) + '"' +
      ' data-option-value="' + escAttr(choice.value) + '"' +
      ' aria-label="' + escAttr(choice.value) + '"' +
      ' aria-pressed="' + (selected ? 'true' : 'false') + '"' +
      (choice.available ? '' : ' disabled aria-disabled="true"');

    if (descriptor.key === 'colour') {
      if (hasExactSwatch) {
        return '<button' + common + '><span class="aykah-wishlist-swatch-dot" style="background-color:' + escAttr(swatch.colour) + '" aria-hidden="true"></span><span class="visually-hidden">' + escHtml(choice.value) + '</span></button>';
      }
      return '<button' + common + '><span class="aykah-wishlist-swatch-name">' + escHtml(choice.value) + '</span></button>';
    }

    return '<button' + common + ' style="--aykah-option-width:' + sizeChoiceWidth(choice.value) + 'px">' +
      '<span class="aykah-wishlist-option-name">' + escHtml(choice.value) + '</span>' +
      '<span class="aykah-wishlist-option-price">' + escHtml(compactPrice(choice.price)) + '</span>' +
    '</button>';
  }

  function optionPickerMarkup(record, descriptors, pickerId) {
    var selectedOptions = {};
    Object.keys(record.item.selectedOptions || {}).forEach(function (key) {
      selectedOptions[key] = record.item.selectedOptions[key];
    });
    Object.keys(record.saved.options || {}).forEach(function (key) {
      selectedOptions[key] = record.saved.options[key];
    });

    var titleId = pickerId + '-title';
    var sections = descriptors.map(function (descriptor) {
      var choices = descriptorValues(record.product, record.item.descriptors, descriptor, selectedOptions);
      var current = selectedOptions[descriptor.key] || '';
      var choicesMarkup = choices.map(function (choice) {
        return optionButtonMarkup(choice, descriptor, current, record.swatches || {});
      }).join('');

      if (descriptor.key === 'colour') {
        return '<div class="aykah-wishlist-option-section is-colour" data-option-section="colour">' +
          '<div class="aykah-wishlist-option-label"><span>COLOUR</span><span class="aykah-wishlist-option-current" data-option-current>' + (current ? '— ' + escHtml(current) : '') + '</span></div>' +
          '<div class="aykah-wishlist-colour-choices">' + choicesMarkup + '</div>' +
        '</div>';
      }

      return '<div class="aykah-wishlist-option-section is-size' + (descriptors.length > 1 ? ' has-label' : '') + '" data-option-section="' + escAttr(descriptor.key) + '">' +
        (descriptors.length > 1 ? '<div class="aykah-wishlist-option-label"><span>' + escHtml(descriptor.key.toUpperCase()) + '</span></div>' : '') +
        '<div class="aykah-wishlist-size-choices">' + choicesMarkup + '</div>' +
      '</div>';
    }).join('');

    return '<h3 class="aykah-wishlist-option-title" id="' + escAttr(titleId) + '">' + escHtml(pickerTitle(descriptors)) + '</h3>' + sections;
  }

  function closeOptionPicker(returnFocus, immediate) {
    if (!activePicker) return;
    var closing = activePicker;
    activePicker = null;
    if (pickerCloseTimer) window.clearTimeout(pickerCloseTimer);
    pickerCloseTimer = null;
    closing.element.classList.remove('is-open', 'is-flipped');
    closing.item.classList.remove('is-picker-open');
    closing.button.setAttribute('aria-expanded', 'false');

    var finish = function () {
      closing.element.hidden = true;
      closing.element.innerHTML = '';
      pickerCloseTimer = null;
    };
    if (immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
    else pickerCloseTimer = window.setTimeout(finish, 200);
    if (returnFocus && closing.button && closing.button.isConnected) closing.button.focus({ preventScroll: true });
  }

  function positionOptionPicker(picker, item, button) {
    var scrollEl = drawer.querySelector('.aykah-wishlist-scroll');
    if (!scrollEl) return;

    var scrollRect = scrollEl.getBoundingClientRect();
    var footerTop = footerEl ? footerEl.getBoundingClientRect().top : scrollRect.bottom;
    var itemRect = item.getBoundingClientRect();
    var buttonRect = button.getBoundingClientRect();
    var pickerHeight = picker.offsetHeight;
    var gap = 8;
    var visibleTop = scrollRect.top + gap;
    var visibleBottom = Math.min(scrollRect.bottom, footerTop) - gap;
    var belowTop = buttonRect.bottom + gap;
    var aboveTop = buttonRect.top - pickerHeight - gap;
    var desiredTop;
    var flipped = false;

    if (belowTop + pickerHeight <= visibleBottom) {
      desiredTop = belowTop;
    } else if (aboveTop >= visibleTop) {
      desiredTop = aboveTop;
      flipped = true;
    } else {
      desiredTop = Math.max(visibleTop, Math.min(belowTop, visibleBottom - pickerHeight));
      flipped = desiredTop < buttonRect.top;
    }

    picker.style.top = Math.round(desiredTop - itemRect.top) + 'px';
    picker.style.bottom = 'auto';
    picker.classList.toggle('is-flipped', flipped);
  }

  function openOptionPicker(recordKey, requestedKeys, skipSwatchFetch) {
    var record = productRecords[recordKey];
    var item = findItemElement(recordKey);
    if (!record || !item) return;
    var button = item.querySelector('.wishlist-drawer-choose');
    var picker = item.querySelector('.aykah-wishlist-option-picker');
    if (!button || !picker) return;

    if (activePicker && activePicker.recordKey === recordKey && !requestedKeys) {
      closeOptionPicker(true);
      return;
    }
    closeOptionPicker(false, true);
    closeShareSheet(false);

    var keys = requestedKeys && requestedKeys.length
      ? requestedKeys.slice()
      : record.item.pickerDescriptors.map(function (descriptor) { return descriptor.key; });
    var descriptors = keys.map(function (key) {
      return record.item.descriptors.find(function (descriptor) { return descriptor.key === key; });
    }).filter(Boolean);
    if (!canUseQuickPicker(descriptors)) return;

    var needsColourSwatches = descriptors.some(function (descriptor) { return descriptor.key === 'colour'; });
    if (needsColourSwatches && record.swatchesState !== 'loaded' && !skipSwatchFetch) {
      var originalLabel = button.textContent;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = 'Loading colours…';

      fetchProductSwatches(record.saved.handle).then(
        function (swatches) {
          record.swatches = swatches;
          record.swatchesState = 'loaded';
        },
        function () {
          record.swatches = {};
          record.swatchesState = 'failed';
        }
      ).then(function () {
        if (button.isConnected) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
          button.textContent = originalLabel;
        }
        if (!isOpen || productRecords[recordKey] !== record) return;
        openOptionPicker(recordKey, requestedKeys, true);
      });
      return;
    }

    var kind = descriptors.length > 1 ? 'is-both' : descriptors[0].key === 'colour' ? 'is-colour-only' : 'is-size-only';
    picker.className = 'aykah-wishlist-option-picker ' + kind;
    picker.innerHTML = optionPickerMarkup(record, descriptors, picker.id);
    picker.setAttribute('aria-labelledby', picker.id + '-title');
    picker.hidden = false;
    item.classList.add('is-picker-open');
    button.setAttribute('aria-expanded', 'true');
    activePicker = {
      recordKey: recordKey,
      element: picker,
      item: item,
      button: button,
      keys: keys
    };

    window.requestAnimationFrame(function () {
      if (!activePicker || activePicker.element !== picker) return;
      positionOptionPicker(picker, item, button);
      picker.classList.add('is-open');
      var missingKey = record.item.pickerDescriptors.length ? record.item.pickerDescriptors[0].key : descriptors[0].key;
      var preferred = picker.querySelector('[data-option-key="' + missingKey + '"]:not([disabled])');
      var first = preferred || picker.querySelector('.aykah-wishlist-option-choice:not([disabled])');
      if (first) first.focus({ preventScroll: true });
    });
  }

  function replaceItemElement(recordKey, itemModel) {
    var current = findItemElement(recordKey);
    if (!current) return null;
    var template = document.createElement('template');
    template.innerHTML = itemMarkup(itemModel);
    var replacement = template.content.firstElementChild;
    if (!replacement) return null;
    replacement.classList.add('is-option-updated');
    current.replaceWith(replacement);
    window.setTimeout(function () {
      if (replacement.isConnected) replacement.classList.remove('is-option-updated');
    }, 220);
    return replacement;
  }

  function applyOptionChoice(recordKey, key, value) {
    var record = productRecords[recordKey];
    if (!record) return;
    var wishlist = getWishlist();
    var index = wishlist.findIndex(function (saved) { return wishlistRecordKey(saved) === recordKey; });
    if (index < 0) return;

    var saved = normalizeSaved(wishlist[index]);
    var options = {};
    Object.keys(record.item.selectedOptions || {}).forEach(function (optionKeyName) {
      options[optionKeyName] = record.item.selectedOptions[optionKeyName];
    });
    Object.keys(saved.options || {}).forEach(function (optionKeyName) {
      options[optionKeyName] = saved.options[optionKeyName];
    });
    options[key] = value;
    saved.options = normalizeOptions(options);
    saved.legacy = false;

    var variant = bestMatchingVariant(record.product, record.item.descriptors, saved.options, '', '');

    // Keep the customer's new choice when an older saved choice conflicts.
    // The incompatible selectable value is cleared and requested again.
    if (!variant || !variant.available) {
      var compatibleOptions = {};
      record.item.descriptors.forEach(function (descriptor) {
        if (descriptor.key === key && saved.options[descriptor.key]) {
          compatibleOptions[descriptor.key] = saved.options[descriptor.key];
          return;
        }
        if (!descriptorIsSelectable(record.product, descriptor) && saved.options[descriptor.key]) {
          compatibleOptions[descriptor.key] = saved.options[descriptor.key];
        }
      });
      saved.options = normalizeOptions(compatibleOptions);
      variant = bestMatchingVariant(record.product, record.item.descriptors, saved.options, '', '');
    }

    saved.variantId = variant ? parseInt(variant.id, 10) || null : null;
    var missing = record.item.descriptors.filter(function (descriptor) {
      return descriptorIsSelectable(record.product, descriptor) && !saved.options[descriptor.key];
    });
    saved.partial = missing.length > 0;

    var resumeKeys = activePicker ? activePicker.keys.slice() : [];
    closeOptionPicker(false, true);
    wishlist[index] = saved;
    var nextWishlist = normalizeWishlist(wishlist);
    var nextRecordKey = wishlistRecordKey(saved);
    var collidedWithSavedVariant = nextWishlist.length < wishlist.length;
    var updatedItem = productViewModel(record.product, saved);
    record.saved = saved;
    record.item = updatedItem;
    localStorage.setItem('aykah_wishlist', JSON.stringify(nextWishlist.map(serializableSaved)));
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { source: 'option-picker' } }));

    /* Changing a partial row into an exact variant changes its identity. If the
       chosen variant was already saved, normalizeWishlist merges the duplicate
       and a clean render avoids showing two copies of the same variation. */
    if (collidedWithSavedVariant) {
      renderItems();
      return;
    }

    if (nextRecordKey !== recordKey) {
      delete productRecords[recordKey];
      productRecords[nextRecordKey] = record;
    }
    replaceItemElement(recordKey, updatedItem);
    var currentItems = Object.keys(productRecords).map(function (recordHandle) {
      return productRecords[recordHandle].item;
    });
    var hasNotice = setNotice(currentItems);
    if (itemsEl) itemsEl.classList.toggle('no-notice', !hasNotice);
    updateFooter(
      wishlist.length,
      currentItems.filter(function (item) { return item.ready; })
    );

    if (missing.length && resumeKeys.length > 1) {
      window.requestAnimationFrame(function () {
        openOptionPicker(nextRecordKey, resumeKeys);
      });
    } else {
      window.requestAnimationFrame(function () {
        var updated = findItemElement(nextRecordKey);
        var action = updated && updated.querySelector('.aykah-wishlist-row-action');
        if (action) action.focus({ preventScroll: true });
      });
    }
  }

  function updatePickerHoverLabel(choice) {
    if (!activePicker || !choice) return;
    var key = choice.getAttribute('data-option-key');
    if (key !== 'colour') return;
    var label = activePicker.element.querySelector('[data-option-section="colour"] [data-option-current]');
    if (label) label.textContent = '— ' + choice.getAttribute('data-option-value');
  }

  function titleFromHandle(handle) {
    return String(handle || 'Saved item')
      .replace(/[-_]+/g, ' ')
      .replace(/\b\w/g, function (letter) { return letter.toUpperCase(); });
  }

  function temporaryItemMarkup(saved) {
    var handle = saved.handle;
    var recordKey = wishlistRecordKey(saved);
    var productUrl = '/products/' + encodeURIComponent(handle);
    var title = titleFromHandle(handle);
    var savedOptions = Object.keys(saved.options || {}).map(function (key) { return saved.options[key]; }).filter(Boolean);
    var savedSummary = savedOptions.length ? savedOptions.join(' · ') : '';

    return '<article class="aykah-wishlist-item is-unavailable" data-wishlist-item="' + escAttr(recordKey) + '">' +
      '<a href="' + productUrl + '" class="aykah-wishlist-thumb" aria-label="' + escAttr(title) + '">' +
        '<span class="aykah-wishlist-thumb-placeholder">' + IMAGE_ICON + '</span>' +
      '</a>' +
      '<a href="' + productUrl + '" class="aykah-wishlist-name">' + escHtml(title) + '</a>' +
      (savedSummary ? '<div class="aykah-wishlist-variant">' + escHtml(savedSummary) + '</div>' : '') +
      '<div class="aykah-wishlist-variant is-missing" role="status">Couldn’t load right now</div>' +
      '<div class="aykah-wishlist-price"></div>' +
      '<div class="aykah-wishlist-actions">' +
        '<button type="button" class="aykah-wishlist-row-action wishlist-drawer-retry" data-wishlist-key="' + escAttr(recordKey) + '" aria-label="Try loading ' + escAttr(title) + ' again">Try again</button>' +
        '<button type="button" class="aykah-wishlist-remove wishlist-drawer-remove" data-wishlist-key="' + escAttr(recordKey) + '" aria-label="Remove this ' + escAttr(title) + ' variation from wishlist">Remove</button>' +
      '</div>' +
    '</article>';
  }

  function setNotice(items) {
    if (!noticeEl || !noticeTextEl) return false;
    var changed = items.find(function (item) { return item.hasDrop; });
    if (!changed) {
      noticeEl.hidden = true;
      noticeTextEl.textContent = '';
      return false;
    }
    noticeTextEl.textContent = changed.title + ' dropped ' + formatDropAmount(changed.dropAmount);
    noticeEl.hidden = false;
    return true;
  }

  function updateFooter(total, ready) {
    readyItems = ready.slice();
    if (!footerEl) return;
    footerEl.classList.toggle('is-empty', total === 0);
    if (footerCountEl) {
      footerCountEl.textContent = total + ' saved · ' + ready.length + ' ready to add';
    }
    if (bulkBtn) {
      bulkBtn.hidden = total === 0 || ready.length === 0;
      bulkBtn.disabled = false;
      bulkBtn.textContent = ready.length === 1 ? 'Add 1 to cart' : 'Add ' + ready.length + ' to cart';
    }
  }

  function showLoading(show) {
    if (loadingEl) loadingEl.hidden = !show;
  }

  function showEmpty(show) {
    if (emptyEl) emptyEl.hidden = !show;
    if (itemsEl && show) itemsEl.hidden = true;
    if (noticeEl && show) noticeEl.hidden = true;
    updateFooter(show ? 0 : getWishlist().length, show ? [] : readyItems);
  }

  function restoreRemovalFocus() {
    var recordKey = pendingRemovalFocusKey;
    var useUndo = pendingRemovalFocusUndo;
    if (!recordKey && !useUndo) return;

    pendingRemovalFocusKey = null;
    pendingRemovalFocusUndo = false;

    window.requestAnimationFrame(function () {
      var item = recordKey ? findItemElement(recordKey) : null;
      var target = item && item.querySelector('.wishlist-drawer-remove, .aykah-wishlist-row-action, .aykah-wishlist-name, .aykah-wishlist-thumb');
      if (target && typeof target.focus === 'function') target.focus();
      else if (undoBtn && !undoBtn.hidden) undoBtn.focus();
    });
  }

  function renderItems() {
    var token = ++renderToken;
    var savedItems = getWishlist();
    if (activePicker) closeOptionPicker(false, true);
    updateCount(savedItems.length);

    if (!savedItems.length) {
      pickerResume = null;
      productRecords = Object.create(null);
      showLoading(false);
      showEmpty(true);
      restoreRemovalFocus();
      return;
    }

    if (emptyEl) emptyEl.hidden = true;
    if (itemsEl) itemsEl.hidden = true;
    if (noticeEl) noticeEl.hidden = true;
    showLoading(true);

    var productRequests = Object.create(null);
    Promise.all(savedItems.map(function (saved) {
      var recordKey = wishlistRecordKey(saved);
      var productRequest = productRequests[saved.handle] || (productRequests[saved.handle] = fetchProduct(saved.handle));
      return productRequest
        .then(function (product) {
          return {
            state: 'loaded',
            recordKey: recordKey,
            handle: saved.handle,
            saved: saved,
            product: product,
            item: productViewModel(product, saved)
          };
        })
        .catch(function (error) {
          return {
            state: error && error.code === 'PRODUCT_NOT_FOUND' ? 'missing' : 'temporary-error',
            recordKey: recordKey,
            handle: saved.handle,
            saved: saved
          };
        });
    })).then(function (results) {
      if (token !== renderToken) return;

      var nextProductRecords = Object.create(null);
      results.forEach(function (result) {
        if (result.state !== 'loaded') return;
        nextProductRecords[result.recordKey] = {
          product: result.product,
          saved: result.saved,
          item: result.item
        };
      });
      productRecords = nextProductRecords;

      var loaded = results
        .filter(function (result) { return result.state === 'loaded'; })
        .map(function (result) { return result.item; });
      var missing = {};
      results.forEach(function (result) {
        if (result.state === 'missing') missing[result.handle] = true;
      });
      var keptItems = savedItems.filter(function (saved) { return !missing[saved.handle]; });

      if (keptItems.length !== savedItems.length) {
        localStorage.setItem('aykah_wishlist', JSON.stringify(keptItems.map(serializableSaved)));
        updateHeaderBadge();
      }

      updateCount(keptItems.length);
      showLoading(false);

      if (!keptItems.length) {
        showEmpty(true);
        return;
      }

      var hasNotice = setNotice(loaded);
      if (itemsEl) {
        itemsEl.innerHTML = results
          .filter(function (result) { return result.state !== 'missing'; })
          .map(function (result) {
            return result.state === 'loaded' ? itemMarkup(result.item) : temporaryItemMarkup(result.saved);
          })
          .join('');
        itemsEl.classList.toggle('no-notice', !hasNotice);
        itemsEl.hidden = false;
      }

      restoreRemovalFocus();

      updateFooter(
        keptItems.length,
        loaded.filter(function (item) { return item.ready; })
      );

      if (pickerResume) {
        var resume = pickerResume;
        pickerResume = null;
        window.requestAnimationFrame(function () {
          openOptionPicker(resume.recordKey, resume.keys);
        });
      }
    });
  }

  var FOCUSABLE_SELECTOR = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  function focusableElements() {
    return Array.prototype.slice.call(drawer.querySelectorAll(FOCUSABLE_SELECTOR)).filter(function (el) {
      return el.offsetParent !== null;
    });
  }

  function trapFocus(e) {
    if (e.key !== 'Tab') return;
    if (activePicker) {
      var pickerFocusable = Array.prototype.slice.call(activePicker.element.querySelectorAll(FOCUSABLE_SELECTOR)).filter(function (el) {
        return el.offsetParent !== null;
      });
      if (pickerFocusable.length) {
        var pickerFirst = pickerFocusable[0];
        var pickerLast = pickerFocusable[pickerFocusable.length - 1];
        if (e.shiftKey && document.activeElement === pickerFirst) {
          e.preventDefault();
          pickerLast.focus();
        } else if (!e.shiftKey && document.activeElement === pickerLast) {
          e.preventDefault();
          pickerFirst.focus();
        }
        return;
      }
    }
    var focusable = focusableElements();
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function wishlistShareUrl() {
    var snapshot = getWishlist().map(function (saved) {
      return saved.handle + ':' + (saved.variantId || '');
    }).join(',');
    return window.location.origin + '/pages/wishlist-shared?items=' + encodeURIComponent(snapshot);
  }

  function copyText(text) {
    if (navigator.clipboard && window.isSecureContext) return navigator.clipboard.writeText(text);
    return new Promise(function (resolve, reject) {
      var field = document.createElement('textarea');
      field.value = text;
      field.setAttribute('readonly', '');
      field.style.cssText = 'position:fixed;left:-9999px;top:0';
      document.body.appendChild(field);
      field.select();
      try {
        if (!document.execCommand('copy')) throw new Error('Copy failed');
        resolve();
      } catch (error) {
        reject(error);
      }
      field.remove();
    });
  }

  function openShareSheet() {
    if (!shareSheet || !shareBtn || getWishlist().length < 1) return;
    closeOptionPicker(false, true);
    if (shareCloseTimer) window.clearTimeout(shareCloseTimer);
    shareCloseTimer = null;
    shareSheet.hidden = false;
    shareBtn.setAttribute('aria-expanded', 'true');
    window.requestAnimationFrame(function () {
      shareSheet.classList.add('is-open');
      var firstAction = shareSheet.querySelector('button');
      if (firstAction) firstAction.focus({ preventScroll: true });
    });
  }

  function closeShareSheet(returnFocus) {
    if (!shareSheet || shareSheet.hidden) return;
    shareSheet.classList.remove('is-open');
    if (shareBtn) shareBtn.setAttribute('aria-expanded', 'false');
    if (shareCloseTimer) window.clearTimeout(shareCloseTimer);
    shareCloseTimer = window.setTimeout(function () {
      shareSheet.hidden = true;
      shareCloseTimer = null;
    }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 200);
    if (returnFocus && shareBtn) shareBtn.focus({ preventScroll: true });
  }

  function toggleShareSheet() {
    if (!shareSheet || shareSheet.hidden) openShareSheet();
    else closeShareSheet(true);
  }

  function copyWishlistLink() {
    return copyText(wishlistShareUrl()).then(function () {
      closeShareSheet(false);
      showSnackbar('Link copied', null, 'success');
    });
  }

  function openDrawer() {
    if (isOpen) return;
    triggerElement = document.activeElement;
    document.dispatchEvent(new CustomEvent('drawer:request-close', { detail: { except: 'wishlist' } }));
    isOpen = true;
    drawer.classList.add('is-open');
    overlay.classList.add('is-open');
    drawer.removeAttribute('inert');
    drawer.setAttribute('aria-hidden', 'false');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    renderItems();
    drawer.addEventListener('keydown', trapFocus);
    setTimeout(function () { closeBtn.focus(); }, 80);
  }

  function closeDrawer() {
    if (!isOpen) return;
    isOpen = false;
    drawer.classList.remove('is-open');
    overlay.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('inert', '');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    drawer.removeEventListener('keydown', trapFocus);
    closeOptionPicker(false, true);
    pickerResume = null;
    closeShareSheet(false);
    hideSnackbar();
    if (triggerElement && triggerElement.focus) triggerElement.focus();
    triggerElement = null;
  }

  function addToCart(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: items }),
      credentials: 'same-origin'
    }).then(function (res) {
      if (!res.ok) throw new Error('Failed to add to cart');
      return res.json();
    });
  }

  function hideSnackbar() {
    if (undoTimer) clearTimeout(undoTimer);
    undoTimer = null;
    if (snackbarEl) snackbarEl.classList.remove('is-visible');
  }

  function showSnackbar(message, before, type) {
    var isSuccess = type === 'success';
    var isRemove = type === 'remove';
    undoSnapshot = before ? before.slice() : null;
    if (snackbarTextEl) snackbarTextEl.textContent = message;
    if (undoBtn) undoBtn.hidden = isSuccess;
    if (snackbarEl) {
      snackbarEl.classList.toggle('is-success', isSuccess);
      snackbarEl.classList.toggle('is-remove', isRemove);
      snackbarEl.classList.add('is-visible');
    }
    if (undoTimer) clearTimeout(undoTimer);
    undoTimer = setTimeout(function () {
      undoSnapshot = null;
      hideSnackbar();
    }, isSuccess ? 2600 : 6000);
  }

  function moveToCart(recordKeys, message) {
    var before = getWishlist();
    var remove = {};
    recordKeys.forEach(function (recordKey) { remove[recordKey] = true; });
    setWishlist(before.filter(function (saved) { return !remove[wishlistRecordKey(saved)]; }));
    showSnackbar(message, before, 'undo');
    document.dispatchEvent(new CustomEvent('cart:updated'));
  }

  function removeRecord(recordKey, element) {
    var before = getWishlist();
    var record = productRecords[recordKey];
    var saved = before.find(function (item) { return wishlistRecordKey(item) === recordKey; });
    var title = record && record.item && record.item.title
      ? record.item.title
      : titleFromHandle(savedHandle(saved)) || 'Item';
    var shouldRestoreFocus = !!(element && element.contains(document.activeElement));

    if (shouldRestoreFocus) {
      var sibling = element.nextElementSibling || element.previousElementSibling;
      pendingRemovalFocusKey = sibling ? sibling.getAttribute('data-wishlist-item') : null;
      pendingRemovalFocusUndo = !pendingRemovalFocusKey;
    }

    if (element) element.classList.add('is-removing');
    setTimeout(function () {
      setWishlist(before.filter(function (item) { return wishlistRecordKey(item) !== recordKey; }));
      showSnackbar(title + ' removed', before, 'remove');
    }, element ? 220 : 0);
  }

  drawer.addEventListener('click', function (e) {
    var optionChoice = e.target.closest('.aykah-wishlist-option-choice');
    if (optionChoice) {
      e.preventDefault();
      if (optionChoice.disabled || !activePicker) return;
      applyOptionChoice(
        activePicker.recordKey,
        optionChoice.getAttribute('data-option-key'),
        optionChoice.getAttribute('data-option-value')
      );
      return;
    }

    var chooseBtn = e.target.closest('.wishlist-drawer-choose');
    if (chooseBtn) {
      e.preventDefault();
      openOptionPicker(chooseBtn.getAttribute('data-wishlist-key'));
      return;
    }

    var retryBtn = e.target.closest('.wishlist-drawer-retry');
    if (retryBtn) {
      e.preventDefault();
      if (retryBtn.disabled) return;
      retryBtn.disabled = true;
      retryBtn.textContent = 'Loading…';
      renderItems();
      return;
    }

    var removeBtn = e.target.closest('.wishlist-drawer-remove');
    if (removeBtn) {
      e.preventDefault();
      removeRecord(removeBtn.getAttribute('data-wishlist-key'), removeBtn.closest('[data-wishlist-item]'));
      return;
    }

    var atcBtn = e.target.closest('.wishlist-drawer-atc');
    if (!atcBtn) return;
    e.preventDefault();
    var variantId = parseInt(atcBtn.getAttribute('data-variant-id'), 10);
    var recordKey = atcBtn.getAttribute('data-wishlist-key');
    var title = atcBtn.getAttribute('data-title') || 'Item';
    var label = atcBtn.querySelector('span');
    if (!variantId || !recordKey) return;

    atcBtn.disabled = true;
    if (label) label.textContent = 'Adding…';
    addToCart([{ id: variantId, quantity: 1 }])
      .then(function () { moveToCart([recordKey], title + ' moved to cart'); })
      .catch(function () {
        if (label) label.textContent = 'Try again';
        setTimeout(function () {
          if (label) label.textContent = 'Add to cart';
          atcBtn.disabled = false;
        }, 1600);
      });
  });

  drawer.addEventListener('pointerover', function (e) {
    updatePickerHoverLabel(e.target.closest('.aykah-wishlist-option-choice'));
  });

  drawer.addEventListener('focusin', function (e) {
    updatePickerHoverLabel(e.target.closest('.aykah-wishlist-option-choice'));
  });

  if (bulkBtn) {
    bulkBtn.addEventListener('click', function () {
      if (!readyItems.length || bulkBtn.disabled) return;
      var adding = readyItems.slice();
      bulkBtn.disabled = true;
      bulkBtn.textContent = 'Adding…';
      addToCart(adding.map(function (item) { return { id: parseInt(item.variantId, 10), quantity: 1 }; }))
        .then(function () {
          var count = adding.length;
          moveToCart(
            adding.map(function (item) { return item.recordKey; }),
            count === 1 ? adding[0].title + ' moved to cart' : count + ' items moved to cart'
          );
        })
        .catch(function () {
          bulkBtn.textContent = 'Try again';
          setTimeout(function () { updateFooter(getWishlist().length, readyItems); }, 1600);
        });
    });
  }

  if (undoBtn) {
    undoBtn.addEventListener('click', function () {
      if (!undoSnapshot) return;
      var restore = undoSnapshot.slice();
      undoSnapshot = null;
      hideSnackbar();
      setWishlist(restore);
    });
  }

  toggleBtn.addEventListener('click', openDrawer);
  closeBtn.addEventListener('click', closeDrawer);
  overlay.addEventListener('click', closeDrawer);

  if (shareBtn) shareBtn.addEventListener('click', toggleShareSheet);

  if (shareCopyBtn) {
    shareCopyBtn.addEventListener('click', function () {
      if (shareCopyBtn.disabled) return;
      shareCopyBtn.disabled = true;
      var copyLabel = shareCopyBtn.querySelector('span');
      copyWishlistLink().catch(function () {
        if (copyLabel) copyLabel.textContent = 'Try again';
        window.setTimeout(function () { if (copyLabel) copyLabel.textContent = 'Copy link'; }, 1600);
      }).then(function () {
        shareCopyBtn.disabled = false;
      });
    });
  }

  if (shareMessageBtn) {
    shareMessageBtn.addEventListener('click', function () {
      var url = wishlistShareUrl();
      var payload = {
        title: 'Aykah wishlist',
        text: 'Take a look at my Aykah wishlist.',
        url: url
      };
      if (navigator.share) {
        navigator.share(payload).then(function () {
          closeShareSheet(false);
        }).catch(function (error) {
          if (!error || error.name !== 'AbortError') {
            var messageLabel = shareMessageBtn.querySelector('span');
            if (messageLabel) messageLabel.textContent = 'Try again';
            window.setTimeout(function () { if (messageLabel) messageLabel.textContent = 'Message'; }, 1600);
          }
        });
      } else {
        copyWishlistLink().catch(function () {});
      }
    });
  }

  document.addEventListener('pointerdown', function (e) {
    if (activePicker && !activePicker.element.contains(e.target) && !activePicker.button.contains(e.target)) {
      closeOptionPicker(false);
    }
    if (shareSheet && !shareSheet.hidden && !shareSheet.contains(e.target) && !(shareBtn && shareBtn.contains(e.target))) {
      closeShareSheet(false);
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' || !isOpen) return;
    if (activePicker) closeOptionPicker(true);
    else if (shareSheet && !shareSheet.hidden) closeShareSheet(true);
    else closeDrawer();
  });

  document.addEventListener('drawer:request-close', function (e) {
    if (e.detail && e.detail.except === 'wishlist') return;
    closeDrawer();
  });

  document.addEventListener('wishlist:updated', function (e) {
    updateHeaderBadge();
    updateCount(getWishlist().length);
    if (isOpen && (!e.detail || e.detail.source !== 'option-picker')) renderItems();
  });

  var swipeStartY = null;
  if (headerEl) {
    headerEl.addEventListener('touchstart', function (e) {
      if (window.innerWidth > 640 || !e.touches || !e.touches.length) return;
      swipeStartY = e.touches[0].clientY;
    }, { passive: true });
    headerEl.addEventListener('touchend', function (e) {
      if (swipeStartY == null || !e.changedTouches || !e.changedTouches.length) return;
      var distance = e.changedTouches[0].clientY - swipeStartY;
      swipeStartY = null;
      if (distance > 72) closeDrawer();
    }, { passive: true });
  }

  if (dragHandle) dragHandle.setAttribute('aria-hidden', 'true');
  updateHeaderBadge();
  updateCount(getWishlist().length);
})();
